import { Body, Heading } from 'copilot-design-system';
import { useMemo } from 'react';

import { validateStringType } from 'src/features/assistant/action-item/ActionItem';
import { AssistantError } from 'src/features/assistant/action-item/AssistantError';
import {
  WidgetContainer,
  WidgetContent,
  WidgetDraft,
  WidgetHeader,
  WidgetItem,
} from 'src/features/assistant/action-item/Widget';
import { useCustomDomains } from 'src/hooks/useCustomDomains';
import {
  useGetFormsQuery,
  useShareFormMutation,
} from 'src/services/api/formsApi';
import { CTA } from 'src/types/stream-chat';
import { DateUtils, UrlUtils } from 'src/utils';

export const ShareFormDraft = ({
  cta,
  onCTAClick,
}: {
  cta: CTA;
  onCTAClick: (cta: CTA) => void;
}) => {
  const formDetails = cta.form_data;
  const {
    data: forms = [],
    error: formsError,
    refetch: refetchForms,
  } = useGetFormsQuery();

  const matchingForm = useMemo(() => {
    return forms.find((form) => form.id === formDetails?.formId);
  }, [forms, formDetails]);

  const [shareForm] = useShareFormMutation();

  const onSendForm = async () => {
    const clientId =
      formDetails && validateStringType(formDetails.clientId)
        ? formDetails.clientId
        : '';
    const companyId =
      formDetails && validateStringType(formDetails.companyId)
        ? formDetails.companyId
        : '';

    const shareFormData = {
      formId:
        formDetails && validateStringType(formDetails.formId)
          ? formDetails.formId
          : '',
      clientCompanyIds: [
        {
          clientId,
          companyId,
        },
      ],
    };

    await shareForm(shareFormData);
  };
  const handleShareForm = async (ctaAction: CTA) => {
    if (formsError) {
      refetchForms();
      return;
    } else {
      await onSendForm();
      onCTAClick(ctaAction);
    }
  };

  const { basePortalDomain } = useCustomDomains();

  const shareFormLink = UrlUtils.GetFullUrl(
    `${basePortalDomain}/sl/form/${matchingForm?.ref}`,
  );

  return (
    <WidgetContainer>
      <WidgetHeader
        widgetName="SHARE FORM"
        buttonName={!matchingForm || formsError ? 'Retry' : 'Share form'}
        buttonIcon={!matchingForm || formsError ? 'Repeat' : 'Send'}
        onClick={() => handleShareForm(cta)}
        copyIcon={matchingForm ? 'Copy' : undefined}
        copyLink={shareFormLink}
      />
      {!matchingForm || formsError ? (
        <AssistantError errorMessage="Unable to load form" />
      ) : (
        <WidgetContent>
          <WidgetDraft>
            <Heading>{matchingForm?.fields.name || 'Form'}</Heading>
          </WidgetDraft>
          <WidgetItem className="border-l-2 border-r-0 border-t-0 border-b-0 border-solid border-non-hover-border pl-4 grid grid-cols-2 auto-rows-[28px] items-center mt-2">
            <Body size="sm" className="text-gray-small">
              Questions
            </Body>
            <Body size="sm">{matchingForm?.fields.formFieldIds.length}</Body>
            <Body size="sm" className="text-gray-small">
              Total submissions
            </Body>
            <Body size="sm">
              {matchingForm?.additionalFields.formResponseSubmissions}
            </Body>
            {matchingForm?.additionalFields.formResponseSubmissions !== 0 && (
              <>
                <Body size="sm" className="text-gray-small">
                  Last submission
                </Body>
                <Body size="sm">
                  {DateUtils.formatDate(
                    matchingForm?.additionalFields.latestSubmissionDate,
                  )}
                </Body>
              </>
            )}
          </WidgetItem>
        </WidgetContent>
      )}
    </WidgetContainer>
  );
};
